import type { ReactNode } from "react";
import { formatMoney, formatPercent, type SerializedMoney } from "./money.js";

/**
 * FinancialFigure / ProvenanceBadge (FIN-01 / UX-02): the ONE way a money figure
 * is shown. It always says
 *   - which currency each amount is in (amounts of different currencies are
 *     listed on separate lines and are never added together),
 *   - whether the figure is OBSERVED (happened, from cost data) or PROJECTED
 *     (an estimate of what could happen),
 *   - how it is known (customer-attested, system-measured, estimated, unverified),
 *   - how fresh it is and how much of the period has data, when known.
 * A bare number never appears on a screen that uses this.
 */

export type Provenance = "estimated" | "customer_attested" | "system_measured" | "unverified";
export type FigureBasis = "observed" | "projected";

const PROVENANCE: Record<Provenance, { label: string; color: string; hint: string }> = {
  customer_attested: { label: "Customer-attested", color: "#195f9c", hint: "A person recorded this with evidence." },
  system_measured: { label: "System-measured", color: "#086b2c", hint: "CoreFinOps measured it from before and after cost data." },
  estimated: { label: "Estimated", color: "#8a6200", hint: "A forecast, not yet confirmed." },
  unverified: { label: "Unverified", color: "#666666", hint: "Recorded without evidence of how it was derived." },
};

export function provenanceLabel(p: string | null | undefined): string {
  return (PROVENANCE as Record<string, { label: string }>)[p ?? ""]?.label ?? "Unverified";
}

export function ProvenanceBadge({ provenance }: { provenance?: string | null | undefined }): ReactNode {
  const key = (provenance && provenance in PROVENANCE ? provenance : "unverified") as Provenance;
  const p = PROVENANCE[key];
  return (
    <span
      className="cfo-prov"
      title={p.hint}
      data-provenance={key}
      style={{ display: "inline-block", fontSize: "0.7rem", fontWeight: 600, color: p.color, border: `1px solid ${p.color}55`, background: `${p.color}1f`, borderRadius: 999, padding: "1px 9px", lineHeight: 1.5 }}
    >
      {p.label}
    </span>
  );
}

export interface FinancialFigureProps {
  readonly label: string;
  readonly amounts: readonly SerializedMoney[];
  readonly basis: FigureBasis;
  readonly provenance?: string | null | undefined;
  /** Day the underlying data runs to (YYYY-MM-DD). */
  readonly asOf?: string | null | undefined;
  /** Share of the period that has data, 0..1 as a string like "0.7742". */
  readonly coverage?: string | null | undefined;
  /** "/ mo", "/ yr" ... appended to each amount. */
  readonly per?: string | undefined;
  readonly note?: ReactNode;
}

export function FinancialFigure({ label, amounts, basis, provenance, asOf, coverage, per, note }: FinancialFigureProps): ReactNode {
  return (
    <figure className="cfo-figure" data-basis={basis} aria-label={label} style={{ margin: 0 }}>
      <figcaption style={{ fontSize: "0.8rem", color: "var(--cfo-muted, #667)" }}>{label}</figcaption>
      {amounts.length === 0 ? (
        <div className="cfo-figure-value" style={{ fontSize: "1.3rem", fontWeight: 650 }}>—</div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {amounts.map((m) => (
            <li key={m.currency} className="cfo-figure-value" style={{ fontSize: "1.3rem", fontWeight: 650 }}>
              {formatMoney(m)}
              {per ? <span style={{ fontSize: "0.8rem", fontWeight: 400 }}> {per}</span> : null}
              {amounts.length > 1 ? <span style={{ fontSize: "0.75rem", fontWeight: 400 }}> {m.currency}</span> : null}
            </li>
          ))}
        </ul>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginTop: 4, fontSize: "0.75rem" }}>
        <span className="cfo-basis" style={{ fontWeight: 600 }}>{basis === "observed" ? "Observed" : "Projected"}</span>
        {provenance !== undefined ? <ProvenanceBadge provenance={provenance} /> : null}
        {asOf ? <span className="cfo-muted">as of {asOf}</span> : null}
        {coverage ? <span className="cfo-muted">{formatPercent(coverage, 0)} of days have data</span> : null}
      </div>
      {amounts.length > 1 ? <div className="cfo-muted" style={{ fontSize: "0.72rem" }}>Different currencies are never added together.</div> : null}
      {note ? <div className="cfo-muted" style={{ fontSize: "0.75rem" }}>{note}</div> : null}
    </figure>
  );
}

/** Attested / measured / unverified side by side for one verified-value total. */
export function ProvenanceBreakdown({ attested, measured, unverified }: { attested: readonly SerializedMoney[]; measured: readonly SerializedMoney[]; unverified?: readonly SerializedMoney[] | undefined }): ReactNode {
  const fmt = (l: readonly SerializedMoney[]) => (l.length ? l.map((m) => formatMoney(m)).join(" · ") : "—");
  return (
    <dl className="cfo-prov-breakdown" style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 10px", margin: 0, fontSize: "0.78rem" }}>
      <dt><ProvenanceBadge provenance="customer_attested" /></dt>
      <dd style={{ margin: 0 }}>{fmt(attested)}</dd>
      <dt><ProvenanceBadge provenance="system_measured" /></dt>
      <dd style={{ margin: 0 }}>{fmt(measured)}</dd>
      {unverified && unverified.length ? (
        <>
          <dt><ProvenanceBadge provenance="unverified" /></dt>
          <dd style={{ margin: 0 }}>{fmt(unverified)}</dd>
        </>
      ) : null}
    </dl>
  );
}
